import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import LandingHeader from '../components/organisms/LandingHeader';
import Button from '../components/atoms/Button';
import Icon from '../components/atoms/Icon';
import SessionManager from '../utils/sessionManager';
import { getCandidateProfile } from '../services/api';
import CreditCostBadge from '../components/molecules/CreditCostBadge';
import useCredits from '../hooks/useCredits';
import { API_BASE_URL } from '../config';

const AITrainerPageRedesigned = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentSession, setCurrentSession] = useState(null);
  const [candidateProfile, setCandidateProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const { balance, hasEnoughCredits, getCost, deductCreditsForService } = useCredits();
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [isProfileComplete, setIsProfileComplete] = useState(true);
  const [profileData, setProfileData] = useState(null);

  const initialMode = location.state?.mode === 'live' ? 'live' : 'normal';
  const [trainingMode, setTrainingMode] = useState(initialMode);

  const [selectedRole, setSelectedRole] = useState('');
  const [customRoleTitle, setCustomRoleTitle] = useState('');
  const [customJD, setCustomJD] = useState('');
  const [uploadedJD, setUploadedJD] = useState(null);
  const [difficultyLevel, setDifficultyLevel] = useState('medium');
  const [duration, setDuration] = useState(10);
  const [numQuestions, setNumQuestions] = useState(10);
  const [resumeFile, setResumeFile] = useState(null);
  const [resumeText, setResumeText] = useState('');
  const [selectedVoice, setSelectedVoice] = useState('female-1');
  const [focusAreas, setFocusAreas] = useState({
    timeManagement: true,
    difficultyLevel: true,
    questionCount: true,
    detailedFeedback: true,
  });

  const voiceOptions = [
    { value: 'female-1', label: 'Female - Expressive (US)', description: 'Breezy, expressive female voice' },
    { value: 'female-2', label: 'Female - Authoritative (UK)', description: 'Firm, professional female voice' },
    { value: 'male-1', label: 'Male - Energetic (US)', description: 'Upbeat, friendly male voice' },
    { value: 'male-2', label: 'Male - Professional (UK)', description: 'Calm, professional male voice' },
    { value: 'male-indian-1', label: 'Male - Indian Accent', description: 'Upbeat male voice with Indian accent' },
  ];

  const roles = [
    { value: 'software_engineer', label: 'Software Engineer', icon: 'code' },
    { value: 'data_scientist', label: 'Data Scientist', icon: 'analytics' },
    { value: 'product_manager', label: 'Product Manager', icon: 'inventory' },
    { value: 'business_analyst', label: 'Business Analyst', icon: 'assessment' },
    { value: 'marketing_manager', label: 'Marketing Manager', icon: 'campaign' },
    { value: 'sales_executive', label: 'Sales Executive', icon: 'trending_up' },
    { value: 'hr_manager', label: 'HR Manager', icon: 'people' },
    { value: 'financial_analyst', label: 'Financial Analyst', icon: 'account_balance' },
    { value: 'ui_ux_designer', label: 'UI/UX Designer', icon: 'design_services' },
    { value: 'project_manager', label: 'Project Manager', icon: 'task' },
    { value: 'other', label: 'Other (Custom Role)', icon: 'edit' }
  ];

  useEffect(() => {
    const session = SessionManager.getCurrentSession();
    if (!session || session.role !== 'candidate') {
      navigate('/login');
      return;
    }
    setCurrentSession(session);
    loadCandidateProfile(session.id);
    checkProfileCompletion();
  }, [navigate]);

  const checkProfileCompletion = async () => {
    const session = SessionManager.getCurrentSession();
    if (!session) return;
    try {
      const response = await fetch(`${API_BASE_URL}/candidates/${session.user_id}`, {
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const profile = data.profile || data;
        setProfileData(profile);
        const hasBasicInfo = profile.full_name && profile.phone;
        const hasDetails = (profile.education && profile.education.length > 0) ||
                          (profile.work_experience && profile.work_experience.length > 0) ||
                          (profile.skills && profile.skills.length > 0);
        setIsProfileComplete(hasBasicInfo && hasDetails);
      }
    } catch (error) {
      console.error('Error checking profile:', error);
    }
  };

  const loadCandidateProfile = async (candidateId) => {
    try {
      const profile = await getCandidateProfile(candidateId);
      setCandidateProfile(profile);
      if (profile?.resume_text) setResumeText(profile.resume_text);
    } catch (error) {
      console.error('Error loading candidate profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleJDUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadedJD(file);
      const reader = new FileReader();
      reader.onload = (event) => setCustomJD(event.target.result);
      reader.readAsText(file);
    }
  };

  const handleResumeUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setResumeFile(file);
      const reader = new FileReader();
      reader.onload = (event) => setResumeText(event.target.result);
      reader.readAsText(file);
    }
  };

  const toggleFocusArea = (key) => {
    setFocusAreas(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleStartTraining = async () => {
    if (!selectedRole) { alert('Please select a role'); return; }
    if (selectedRole === 'other' && !customRoleTitle.trim()) { alert('Please enter a custom role title'); return; }

    const serviceType = trainingMode === 'live' ? 'ai_live_trainer' : 'ai_trainer';
    if (!hasEnoughCredits(serviceType)) { setShowCreditModal(true); return; }

    const creditResult = await deductCreditsForService(serviceType);
    if (!creditResult.success) { setShowCreditModal(true); return; }

    const selectedRoleData = roles.find(r => r.value === selectedRole);
    const jobTitle = selectedRole === 'other' ? customRoleTitle.trim() : selectedRoleData.label;

    const trainingData = {
      trainingMode, sessionType: 'job_specific', jobTitle,
      jobDescription: customJD || `Generic ${jobTitle} interview questions`,
      difficultyLevel,
      numQuestions: trainingMode === 'normal' ? numQuestions : null,
      duration: trainingMode === 'live' ? duration : null,
      resumeText: resumeText || candidateProfile?.resume_text || '',
      resumeFilename: candidateProfile?.resume_filename || resumeFile?.name || null,
      hasResume: !!(resumeText || candidateProfile?.resume_url || resumeFile),
      candidateName: candidateProfile?.full_name || `${currentSession?.profile?.first_name} ${currentSession?.profile?.last_name}`,
      candidateEmail: currentSession?.email,
      candidateId: currentSession?.user_id || '',
      voice: selectedVoice,
    };

    localStorage.setItem('ai_trainer_data', JSON.stringify(trainingData));

    if (trainingMode === 'live') {
      navigate(`/live-interview/training_${Date.now()}`, { state: { interviewType: 'trainer', ...trainingData } });
    } else {
      navigate('/ai-trainer/welcome', { state: trainingData });
    }
  };

  const isFormValid = selectedRole && (selectedRole !== 'other' || customRoleTitle.trim());

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <LandingHeader />
        <div className="flex items-center justify-center h-[60vh]">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-900 border-t-transparent"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <LandingHeader />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-16">
        {/* Header */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <button
                onClick={() => navigate(-1)}
                className="w-9 h-9 rounded-lg border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors"
              >
                <Icon name="arrow_back" size={18} />
              </button>
              <h1 className="text-2xl font-bold text-gray-900">
                Configure Interview
              </h1>
            </div>
            <p className="text-gray-500 text-sm ml-12">
              Set up your AI-powered mock interview session
            </p>
          </div>
          <CreditCostBadge
            serviceType={trainingMode === 'live' ? 'ai_live_trainer' : 'ai_trainer'}
            serviceName={trainingMode === 'live' ? 'Live Interview' : 'Mock Interview'}
            size="md"
          />
        </div>

        {/* Mode Toggle */}
        <div className="bg-white border border-gray-200 rounded-xl p-1 inline-flex mb-8">
          <button
            onClick={() => setTrainingMode('normal')}
            className={`px-6 py-2.5 rounded-lg text-sm font-medium transition-all ${
              trainingMode === 'normal'
                ? 'bg-[#0a1929] text-white'
                : 'text-gray-500 hover:text-gray-900'
            }`}
          >
            <span className="flex items-center gap-2">
              <Icon name="edit_note" size={18} />
              Mock Interview
            </span>
          </button>
          <button
            onClick={() => setTrainingMode('live')}
            className={`px-6 py-2.5 rounded-lg text-sm font-medium transition-all ${
              trainingMode === 'live'
                ? 'bg-[#0a1929] text-white'
                : 'text-gray-500 hover:text-gray-900'
            }`}
          >
            <span className="flex items-center gap-2">
              <Icon name="videocam" size={18} />
              Live Interview
              <span className="text-[10px] font-bold bg-green-500 text-white px-1.5 py-0.5 rounded leading-none">NEW</span>
            </span>
          </button>
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">

          {/* LEFT - Resume & JD */}
          <div className="lg:col-span-7 space-y-6">

            {/* Resume */}
            <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-[#0a1929] rounded-lg flex items-center justify-center">
                    <Icon name="description" size={18} className="text-white" />
                  </div>
                  <div>
                    <h2 className="text-sm font-semibold text-gray-900">Your Resume</h2>
                    <p className="text-xs text-gray-400">Used to personalize interview questions</p>
                  </div>
                </div>
                <button
                  onClick={() => document.getElementById('resume-upload').click()}
                  className="text-xs font-medium text-gray-900 border border-gray-200 px-3 py-1.5 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-1.5"
                >
                  <Icon name="upload" size={14} />
                  Upload
                </button>
              </div>

              <div className="p-6">
                <input type="file" id="resume-upload" accept=".pdf,.doc,.docx,.txt" onChange={handleResumeUpload} className="hidden" />

                {candidateProfile?.resume_url || resumeText ? (
                  <>
                    <div className="flex items-center gap-2 px-3 py-2.5 bg-green-50 border border-green-200 rounded-lg mb-4">
                      <Icon name="check_circle" size={16} className="text-green-600" />
                      <span className="text-xs font-medium text-green-700 flex-1">
                        {candidateProfile?.resume_filename || resumeFile?.name || 'Resume loaded from profile'}
                      </span>
                      <span className="text-[10px] font-bold text-green-600 bg-green-100 px-2 py-0.5 rounded">READY</span>
                    </div>
                    <textarea
                      value={resumeText}
                      onChange={(e) => setResumeText(e.target.value)}
                      placeholder="Your resume content..."
                      rows={9}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-900 text-sm leading-relaxed focus:ring-1 focus:ring-gray-300 focus:border-gray-300 focus:bg-white transition-all resize-none"
                    />
                    <p className="text-xs text-gray-400 mt-2">{resumeText.split(/\s+/).filter(Boolean).length} words detected</p>
                  </>
                ) : (
                  <label
                    htmlFor="resume-upload"
                    className="flex flex-col items-center justify-center gap-3 py-12 border-2 border-dashed border-gray-200 rounded-xl cursor-pointer hover:border-gray-400 hover:bg-gray-50/50 transition-all group"
                  >
                    <div className="w-14 h-14 rounded-2xl bg-gray-100 group-hover:bg-gray-200 flex items-center justify-center transition-colors">
                      <Icon name="upload_file" size={28} className="text-gray-400" />
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-medium text-gray-700">
                        Drop your resume here or <span className="text-gray-900 underline underline-offset-2">browse</span>
                      </p>
                      <p className="text-xs text-gray-400 mt-1">PDF, DOC, DOCX, or TXT (max 5MB)</p>
                    </div>
                  </label>
                )}
              </div>
            </div>

            {/* Job Description */}
            <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-[#0a1929] rounded-lg flex items-center justify-center">
                    <Icon name="work" size={18} className="text-white" />
                  </div>
                  <div>
                    <h2 className="text-sm font-semibold text-gray-900">Job Description</h2>
                    <p className="text-xs text-gray-400">Optional - helps generate targeted questions</p>
                  </div>
                </div>
                <button
                  onClick={() => document.getElementById('jd-upload').click()}
                  className="text-xs font-medium text-gray-900 border border-gray-200 px-3 py-1.5 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-1.5"
                >
                  <Icon name="attach_file" size={14} />
                  Import
                </button>
              </div>

              <div className="p-6">
                <input type="file" id="jd-upload" accept=".pdf,.doc,.docx,.txt" onChange={handleJDUpload} className="hidden" />
                <textarea
                  value={customJD}
                  onChange={(e) => setCustomJD(e.target.value)}
                  placeholder="Paste the job description or role requirements here..."
                  rows={9}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-900 text-sm leading-relaxed focus:ring-1 focus:ring-gray-300 focus:border-gray-300 focus:bg-white transition-all resize-none"
                />
              </div>
            </div>
          </div>

          {/* RIGHT - Settings */}
          <div className="lg:col-span-5 space-y-6">

            {/* Target Role */}
            <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-[#0a1929] rounded-lg flex items-center justify-center">
                    <Icon name="person_search" size={18} className="text-white" />
                  </div>
                  <h2 className="text-sm font-semibold text-gray-900">Target Role</h2>
                </div>
              </div>
              <div className="p-6">
                <select
                  value={selectedRole}
                  onChange={(e) => {
                    setSelectedRole(e.target.value);
                    if (e.target.value !== 'other') setCustomRoleTitle('');
                  }}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-900 text-sm focus:ring-1 focus:ring-gray-300 focus:border-gray-300 focus:bg-white transition-all appearance-none"
                  style={{ backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, backgroundPosition: 'right 12px center', backgroundRepeat: 'no-repeat', backgroundSize: '20px' }}
                >
                  <option value="">Select a job role...</option>
                  {roles.map((role) => (
                    <option key={role.value} value={role.value}>{role.label}</option>
                  ))}
                </select>

                {selectedRole === 'other' && (
                  <input
                    type="text"
                    value={customRoleTitle}
                    onChange={(e) => setCustomRoleTitle(e.target.value)}
                    placeholder="Enter custom role title..."
                    className="w-full mt-3 px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-900 text-sm focus:ring-1 focus:ring-gray-300 focus:border-gray-300 focus:bg-white transition-all"
                  />
                )}

                {!selectedRole && (
                  <p className="text-xs text-gray-400 mt-3 flex items-center gap-1.5">
                    <Icon name="info" size={14} className="text-gray-300" />
                    Required to start
                  </p>
                )}
              </div>
            </div>

            {/* Settings */}
            <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-[#0a1929] rounded-lg flex items-center justify-center">
                    <Icon name="tune" size={18} className="text-white" />
                  </div>
                  <h2 className="text-sm font-semibold text-gray-900">Settings</h2>
                </div>
              </div>
              <div className="p-6 space-y-6">

                {/* Difficulty */}
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3 block">
                    Difficulty
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {['easy', 'medium', 'expert'].map((level) => (
                      <button
                        key={level}
                        onClick={() => setDifficultyLevel(level)}
                        className={`py-2.5 rounded-xl text-sm font-medium transition-all capitalize ${
                          difficultyLevel === level
                            ? 'bg-[#0a1929] text-white'
                            : 'bg-gray-50 text-gray-600 border border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        {level}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Duration / Questions */}
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3 block">
                    {trainingMode === 'live' ? 'Duration' : 'Number of Questions'}
                  </label>
                  {trainingMode === 'live' ? (
                    <div className="grid grid-cols-5 gap-2">
                      {[5, 10, 15, 20, 30].map((val) => (
                        <button
                          key={val}
                          onClick={() => setDuration(val)}
                          className={`py-2.5 rounded-xl text-sm font-medium transition-all ${
                            duration === val
                              ? 'bg-[#0a1929] text-white'
                              : 'bg-gray-50 text-gray-600 border border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          {val}m
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="grid grid-cols-4 gap-2">
                      {[5, 10, 15, 20].map((val) => (
                        <button
                          key={val}
                          onClick={() => setNumQuestions(val)}
                          className={`py-2.5 rounded-xl text-sm font-medium transition-all ${
                            numQuestions === val
                              ? 'bg-[#0a1929] text-white'
                              : 'bg-gray-50 text-gray-600 border border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          {val}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Focus Areas */}
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3 block">
                    Focus Areas
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { key: 'timeManagement', label: 'Time Management' },
                      { key: 'difficultyLevel', label: 'Difficulty Level' },
                      { key: 'questionCount', label: 'Question Coverage' },
                      { key: 'detailedFeedback', label: 'Detailed Feedback' },
                    ].map(({ key, label }) => (
                      <button
                        key={key}
                        onClick={() => toggleFocusArea(key)}
                        className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-xs font-medium transition-all text-left ${
                          focusAreas[key]
                            ? 'bg-[#0a1929] text-white'
                            : 'bg-gray-50 text-gray-600 border border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <Icon name={focusAreas[key] ? 'check_box' : 'check_box_outline_blank'} size={16} />
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Voice - Live only */}
                {trainingMode === 'live' && (
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3 block">
                      Interviewer Voice
                    </label>
                    <select
                      value={selectedVoice}
                      onChange={(e) => setSelectedVoice(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-900 text-sm focus:ring-1 focus:ring-gray-300 focus:border-gray-300 focus:bg-white transition-all"
                    >
                      {voiceOptions.map((voice) => (
                        <option key={voice.value} value={voice.value}>{voice.label}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>

            {/* Start Button */}
            <button
              onClick={handleStartTraining}
              disabled={!isFormValid}
              className="w-full py-4 bg-[#0a1929] hover:bg-[#0d2137] text-white text-sm font-semibold rounded-2xl transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <Icon name={trainingMode === 'live' ? 'videocam' : 'play_arrow'} size={20} />
              {trainingMode === 'live' ? 'Start Live Interview' : 'Start Mock Interview'}
            </button>

            <p className="text-xs text-center text-gray-400">
              Questions are generated based on your role, resume, and job description
            </p>
          </div>
        </div>
      </div>

      {/* Modal */}
      {showCreditModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full overflow-hidden border border-gray-200 shadow-2xl">
            <div className="p-6">
              <div className="flex items-center justify-between mb-5">
                <div className="w-12 h-12 bg-[#0a1929] rounded-xl flex items-center justify-center">
                  <Icon name={!isProfileComplete ? 'person_add' : 'account_balance_wallet'} size={24} className="text-white" />
                </div>
                <button
                  onClick={() => setShowCreditModal(false)}
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:bg-gray-100 transition-colors"
                >
                  <Icon name="close" size={20} />
                </button>
              </div>

              {!isProfileComplete ? (
                <>
                  <h3 className="text-lg font-bold text-gray-900 mb-1">Complete Your Profile</h3>
                  <p className="text-sm text-gray-500 mb-5">Get 50 free credits when you complete your profile.</p>

                  <div className="bg-green-50 border border-green-100 rounded-xl p-4 mb-6 space-y-2.5">
                    {['50 free credits instantly', 'Access to all AI features', 'Better job recommendations'].map((item) => (
                      <div key={item} className="flex items-center gap-2">
                        <Icon name="check_circle" size={16} className="text-green-600" />
                        <span className="text-xs text-green-700 font-medium">{item}</span>
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={() => navigate('/candidate/profile/edit')}
                    className="w-full py-3 bg-[#0a1929] hover:bg-[#0d2137] text-white text-sm font-semibold rounded-xl transition-colors mb-2"
                  >
                    Complete Profile
                  </button>
                  <button
                    onClick={() => setShowCreditModal(false)}
                    className="w-full py-3 text-gray-500 hover:text-gray-900 text-sm font-medium rounded-xl transition-colors"
                  >
                    Maybe Later
                  </button>
                </>
              ) : (
                <>
                  <h3 className="text-lg font-bold text-gray-900 mb-1">Insufficient Credits</h3>
                  <p className="text-sm text-gray-500 mb-5">
                    You need {getCost(trainingMode === 'live' ? 'ai_live_trainer' : 'ai_trainer')} credits for this service.
                  </p>

                  <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 mb-6">
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-sm text-gray-500">Available</span>
                      <span className="text-sm font-bold text-gray-900">{balance} credits</span>
                    </div>
                    <div className="h-px bg-gray-200"></div>
                    <div className="flex justify-between items-center mt-3">
                      <span className="text-sm text-gray-500">Required</span>
                      <span className="text-sm font-bold text-red-600">{getCost(trainingMode === 'live' ? 'ai_live_trainer' : 'ai_trainer')} credits</span>
                    </div>
                  </div>

                  <button
                    onClick={() => navigate('/payment')}
                    className="w-full py-3 bg-[#0a1929] hover:bg-[#0d2137] text-white text-sm font-semibold rounded-xl transition-colors mb-2"
                  >
                    Buy Credits
                  </button>
                  <button
                    onClick={() => setShowCreditModal(false)}
                    className="w-full py-3 text-gray-500 hover:text-gray-900 text-sm font-medium rounded-xl transition-colors"
                  >
                    Cancel
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AITrainerPageRedesigned;
